import * as React from 'react';

import {DisabledSection} from 'shells/disabled-section/disabled-section';
import {FlashBanner} from 'shells/flash-banner/flash-banner';
import {RetryableError} from 'shells/flash-banner/retryable-error';
import {SpacerGroup} from 'shells/spacer/spacer-group';
import {MarketingHeader} from 'shells/typography/marketing-header';
import {MarketingTitle} from 'shells/typography/marketing-title';

import type {UserEmail as UserEmailFragment} from 'nutshell-graphql-types';

import {NotifyMessages} from '../../../core/notify-messages/notify-messages';

import {PinnedInstanceNote} from '../pinned-domains/pinned-instance-note';

import {BccImport} from './bcc-import';
import {EmailBlocklist} from './email-blocklist';
import {EmailSyncAllMessagesSetting} from './email-sync-all-messages-setting';
import {EmailSyncShareSetting} from './email-sync-share-setting';
import {EmailSyncingEmptyState} from './email-syncing-empty-state';
import {EmailWidgetGoogle} from './email-widget-google';
import {EmailWidgetO365} from './email-widget-o365';
import {GlobalEmailBlocklist} from './global-email-blocklist';

type Props = {
    isLoading: boolean;
    allUserEmailAddresses?: UserEmailFragment[];
    refetch: () => Promise<any>;
    errorMessage: string | null | undefined;
};

export function EmailSyncingSettings(props: Props) {
    const [errorMessages, setErrorMessages] = React.useState<string[]>([]);
    // TODO: success messages
    // const [successMessages, setSuccessMessages] = React.useState<React.Element<any>[]>([]);

    const errorBanners = errorMessages.map((message) => (
        <FlashBanner
            key={message}
            hasBorder={true}
            type='error'
            onHidden={() => {
                setErrorMessages(errorMessages.filter((msg) => msg !== message));
            }}
        >
            {message}
        </FlashBanner>
    ));

    if (!props.isLoading && !props.allUserEmailAddresses) {
        return (
            <React.Fragment>
                {errorBanners}
                <RetryableError
                    message='There was a problem getting your email sync settings. Please retry or contact
                    support.'
                    details={props.errorMessage}
                    retry={props.refetch}
                />
            </React.Fragment>
        );
    }

    const userEmailAddressesThatCanAllowSync = props.allUserEmailAddresses
        ? props.allUserEmailAddresses.filter((email) => {
              return email.allowsEmailSync;
          })
        : [];

    if (!props.isLoading && !userEmailAddressesThatCanAllowSync.length) {
        // Empty state
        return (
            <SpacerGroup mb={32}>
                {errorBanners}
                <EmailSyncingEmptyState />
                <BccImport emails={props.allUserEmailAddresses} />
            </SpacerGroup>
        );
    }

    return (
        <DisabledSection disabled={props.isLoading}>
            <NotifyMessages />
            {errorBanners}
            <SpacerGroup mb={32}>
                <MarketingHeader>Email sync connection</MarketingHeader>
                {userEmailAddressesThatCanAllowSync.map((email) => {
                    return email.type === 'GOOGLE' || email.type === 'GOOGLE_APPS' ? (
                        <SpacerGroup key={email.id} mb={16}>
                            <EmailWidgetGoogle
                                onAuthStart={() => {
                                    setErrorMessages([]);
                                }}
                                onAuthSuccess={
                                    (/* msg: ?React.Element<any> */) => {
                                        // if (msg) setSuccessMessages([msg]);

                                        return props.refetch();
                                    }
                                }
                                onAuthError={(errMsg) => {
                                    setErrorMessages(errorMessages.concat(errMsg));
                                }}
                                email={email}
                                errors={email.syncErrors}
                            />
                        </SpacerGroup>
                    ) : (
                        <SpacerGroup key={email.id} mb={16}>
                            <EmailWidgetO365
                                onAuthStart={() => {
                                    setErrorMessages([]);
                                }}
                                onAuthSuccess={
                                    (/* msg: ?React.Element<any> */) => {
                                        // if (msg) setSuccessMessages([msg]);

                                        return props.refetch();
                                    }
                                }
                                onAuthError={(errMsg) => {
                                    setErrorMessages(errorMessages.concat(errMsg));
                                }}
                                email={email}
                                errors={email.syncErrors}
                            />
                        </SpacerGroup>
                    );
                })}
            </SpacerGroup>
            <SpacerGroup mb={32}>
                <MarketingHeader>Privacy settings</MarketingHeader>
                <PinnedInstanceNote />
            </SpacerGroup>
            <SpacerGroup mb={32}>
                <MarketingTitle>
                    Which messages would you like to sync into Nutshell?
                </MarketingTitle>
                <EmailSyncAllMessagesSetting
                    showSavedToast={true}
                    onError={(errorMessage) => {
                        setErrorMessages(errorMessages.concat(errorMessage));
                    }}
                />
            </SpacerGroup>
            <SpacerGroup mb={32}>
                <MarketingTitle>
                    Which messages should be automatically shared with your team?
                </MarketingTitle>
                <EmailSyncShareSetting
                    showSavedToast={true}
                    onError={(errorMessage) => {
                        setErrorMessages(errorMessages.concat(errorMessage));
                    }}
                />
            </SpacerGroup>
            <SpacerGroup mb={32}>
                <GlobalEmailBlocklist />
            </SpacerGroup>
            <SpacerGroup mb={32}>
                <EmailBlocklist
                    onError={(errorMessage) => {
                        setErrorMessages(errorMessages.concat(errorMessage));
                    }}
                />
            </SpacerGroup>
            <BccImport emails={props.allUserEmailAddresses} />
        </DisabledSection>
    );
}
